import { useCallback, useEffect, useRef, type RefObject } from "react";
import { animate, useMotionValue, useReducedMotion, type MotionValue } from "motion/react";
import { useNavigate } from "react-router";

import {
  canCommitEdgeSwipeBack,
  classifyEdgeSwipeIntent,
  clampEdgeSwipeOffset,
  isPointerInEdgeZone,
  resolveEdgeSwipeRelease,
  type EdgeSwipeBackTarget,
  type EdgeSwipeGestureIntent,
} from "./edgeSwipeBackLogic";

const SWIPE_SPRING = { type: "spring" as const, stiffness: 420, damping: 40, mass: 0.82 };

type UseEdgeSwipeBackOptions = {
  enabled?: boolean;
  interactive?: boolean;
  backTarget?: EdgeSwipeBackTarget;
};

type UseEdgeSwipeBackResult = {
  x: MotionValue<number>;
};

/**
 * Left-edge swipe-back with optional follow-finger translation.
 * Settings should pass `backTarget={{ pathname: "/today" }}`; legal pages use `history`.
 */
export function useEdgeSwipeBack(
  containerRef: RefObject<HTMLElement | null>,
  {
    enabled = true,
    interactive = true,
    backTarget = "history",
  }: UseEdgeSwipeBackOptions = {},
): UseEdgeSwipeBackResult {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const x = useMotionValue(0);
  const backCommittedRef = useRef(false);
  const animatingRef = useRef(false);

  const getViewportWidth = useCallback(() => {
    const el = containerRef.current;
    return el?.offsetWidth ?? window.innerWidth;
  }, [containerRef]);

  const commitBack = useCallback(() => {
    if (backCommittedRef.current || !canCommitEdgeSwipeBack(backTarget)) return;
    backCommittedRef.current = true;

    if (backTarget === "history") {
      navigate(-1);
    } else {
      navigate(backTarget.pathname, { replace: backTarget.replace ?? false });
    }

    window.setTimeout(() => {
      backCommittedRef.current = false;
    }, 400);
  }, [backTarget, navigate]);

  const snapToRest = useCallback(
    (animated = true) => {
      if (!animated || prefersReducedMotion || !interactive) {
        x.set(0);
        return;
      }
      void animate(x, 0, SWIPE_SPRING);
    },
    [interactive, prefersReducedMotion, x],
  );

  const finishInteractiveRelease = useCallback(
    async (offsetX: number, velocityX: number) => {
      const width = getViewportWidth();
      const decision = resolveEdgeSwipeRelease(offsetX, velocityX);

      if (decision === "commit" && canCommitEdgeSwipeBack(backTarget)) {
        if (prefersReducedMotion) {
          commitBack();
          x.set(0);
          return;
        }

        animatingRef.current = true;
        await animate(x, width, SWIPE_SPRING);
        commitBack();
        x.set(0);
        animatingRef.current = false;
        return;
      }

      snapToRest(true);
    },
    [backTarget, commitBack, getViewportWidth, prefersReducedMotion, snapToRest, x],
  );

  useEffect(() => {
    if (!enabled) return;

    const el = containerRef.current;
    if (!el) return;

    let pointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let intent: EdgeSwipeGestureIntent = "pending";

    const resetGesture = () => {
      pointerId = null;
      intent = "pending";
    };

    const onPointerDown = (event: PointerEvent) => {
      if (pointerId !== null || animatingRef.current || backCommittedRef.current) return;
      if (event.button !== 0) return;
      if (!isPointerInEdgeZone(event.clientX)) return;
      if (!canCommitEdgeSwipeBack(backTarget)) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startTime = performance.now();
      intent = "pending";
    };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (intent === "pending") {
        const nextIntent = classifyEdgeSwipeIntent(dx, dy);
        if (nextIntent === "pending") return;

        intent = nextIntent;
        if (intent === "back") {
          el.setPointerCapture(event.pointerId);
        } else {
          resetGesture();
          return;
        }
      }

      if (intent === "back") {
        event.preventDefault();
        if (interactive) {
          x.set(clampEdgeSwipeOffset(dx, getViewportWidth()));
        }
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;

      if (intent === "back") {
        const dx = event.clientX - startX;
        const elapsedMs = Math.max(performance.now() - startTime, 1);
        const velocityX = (dx / elapsedMs) * 1000;

        event.preventDefault();

        if (interactive) {
          void finishInteractiveRelease(dx, velocityX);
        } else if (resolveEdgeSwipeRelease(dx, velocityX) === "commit") {
          commitBack();
        }

        try {
          el.releasePointerCapture(event.pointerId);
        } catch {
          // Pointer may already be released.
        }
      }

      resetGesture();
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove, { passive: false });
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
  }, [
    backTarget,
    commitBack,
    containerRef,
    enabled,
    finishInteractiveRelease,
    getViewportWidth,
    interactive,
    x,
  ]);

  return { x };
}
